import { useQuery } from "@tanstack/react-query";
import { CREDENTIAL_SCOPE_LABELS } from "@amber/contracts";
import {
  ActionButton,
  DataTable,
  EmptyState,
  GridEmptyRow,
  LinkButton,
  PopoverHeading,
  Status,
  StatusPopover,
  Tag,
  Toolbar,
  dataColumns,
  errorText,
  formatDate,
  formatDateTime,
  formatRelative,
  type DataColumn,
} from "@amber/ui";
import { ACCESS_ACTIONS, type CredentialRow } from "../contracts/index";
import { credentialsQuery } from "./queries";
import { find } from "./actions";
import type { DialogState } from "./dialogs/AccessDialogs";

const CREDENTIAL_STATE: Record<CredentialRow["state"], { tone: "ok" | "off" | "attention"; label: string }> = {
  active: { tone: "ok", label: "Active" },
  expired: { tone: "off", label: "Expired" },
  revoked: { tone: "off", label: "Revoked" },
};

function CredentialState({ row }: { row: CredentialRow }) {
  const state = CREDENTIAL_STATE[row.state];
  if (!row.revoked) return <Status tone={state.tone}>{state.label}</Status>;
  return (
    <StatusPopover label={<Status tone={state.tone}>{state.label}</Status>} accessibleLabel={`${state.label}, details`}>
      <PopoverHeading>Revoked</PopoverHeading>
      <dl>
        <dt>By</dt>
        <dd>{row.revoked.by}</dd>
        <dt>When</dt>
        <dd>{formatDateTime(row.revoked.at)}</dd>
        <dt>Reason</dt>
        <dd>{row.revoked.reason}</dd>
      </dl>
    </StatusPopover>
  );
}

const col = dataColumns<CredentialRow>();

function credentialColumns(onDialog: (state: DialogState) => void): DataColumn<CredentialRow>[] {
  return [
    col.display({
      id: "name",
      header: "Name",
      meta: { width: 190, stickyCell: true, cellClassName: "font-strong text-fg" },
      cell: ({ row }) => row.original.name,
    }),
    col.display({
      id: "scope",
      header: "Scope",
      meta: { width: 230 },
      cell: ({ row }) => (
        <span className="flex flex-wrap gap-1">
          {row.original.scopes.map((scope) => (
            <Tag key={scope}>{CREDENTIAL_SCOPE_LABELS[scope]}</Tag>
          ))}
        </span>
      ),
    }),
    col.display({
      id: "expires",
      header: "Expires",
      meta: { width: 100 },
      cell: ({ row }) =>
        row.original.expires_at ? formatDate(row.original.expires_at) : <span className="text-muted">Never</span>,
    }),
    col.display({
      id: "issued_by",
      header: "Issued by",
      meta: { width: 140 },
      cell: ({ row }) => (
        <>
          {row.original.issued_by}
          <span className="block text-xs text-muted">{formatDate(row.original.issued_at)}</span>
        </>
      ),
    }),
    col.display({
      id: "last_used",
      header: "Last used",
      meta: { width: 110 },
      cell: ({ row }) =>
        row.original.last_used_at ? (
          formatRelative(row.original.last_used_at)
        ) : (
          <span className="text-muted">Not used yet</span>
        ),
    }),
    col.display({
      id: "state",
      header: "State",
      meta: { width: 100, cellClassName: "overflow-visible" },
      cell: ({ row }) => <CredentialState row={row.original} />,
    }),
    col.display({
      id: "actions",
      meta: { label: "Actions", width: 90, cellClassName: "overflow-visible" },
      cell: ({ row }) =>
        row.original.state === "revoked" ? null : (
          <ActionButton
            action={find(row.original.actions, ACCESS_ACTIONS.revokeCredential)}
            label="Revoke…"
            onClick={() => {
              onDialog({ kind: "revokeCredential", credential: row.original });
            }}
          />
        ),
    }),
  ];
}

export function CredentialsTable({ onDialog }: { onDialog: (state: DialogState) => void }) {
  const credentials = useQuery(credentialsQuery);
  const columns = 7;
  return (
    <>
      <Toolbar>
        <span className="text-md text-muted">
          Consumers and automation read evidence through the machine interface with these credentials. A credential
          can&apos;t sign in to the workspace.
        </span>
      </Toolbar>
      <DataTable
        label="Machine credentials"
        columns={credentialColumns(onDialog)}
        rows={credentials.data?.rows ?? []}
        rowId={(r) => r.id}
        minWidth={940}
        isPending={credentials.isPending}
        skeletonRows={3}
        emptyRow={
          credentials.isError ? (
            <GridEmptyRow columns={columns}>
              Couldn&apos;t load machine credentials. {errorText(credentials.error)}{" "}
              <LinkButton
                onClick={() => {
                  void credentials.refetch();
                }}
              >
                Retry
              </LinkButton>
            </GridEmptyRow>
          ) : (
            <tr>
              <td colSpan={columns} className="border-b border-divider bg-bg">
                <EmptyState title="No machine credentials">
                  Issue one for each consumer that reads events, changes or exports. Its secret is shown once.
                </EmptyState>
              </td>
            </tr>
          )
        }
      />
    </>
  );
}
